import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { qk } from '@/lib/queryKeys';

export type Notification = {
  id: string;
  category_key: string | null;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

const PAGE = 50;

export function useNotifications() {
  return useQuery({
    queryKey: qk.notifications,
    queryFn: async (): Promise<Notification[]> => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, category_key, title, body, link, read_at, created_at')
        .order('created_at', { ascending: false })
        .limit(PAGE);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
  });
}

/** ספירת הלא נקראו לפעמון, בלי למשוך את כל השורות. */
export function useUnreadCount() {
  return useQuery({
    queryKey: [...qk.notifications, 'unread'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30 * 1000,
  });
}

/** זמן אמת על ההתראות שלי, כדי שהפעמון יתעדכן בלי רענון. */
export function useNotificationsRealtime(employeeId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!employeeId) return;

    const channel = supabase
      .channel(`notifications:${employeeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `employee_id=eq.${employeeId}`,
        },
        () => void queryClient.invalidateQueries({ queryKey: qk.notifications }),
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [employeeId, queryClient]);
}

export function useNotificationMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: qk.notifications });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { markRead, markAllRead };
}
